import { z } from 'zod';

export const EVENT_TYPES = ['formula', 'sleep', 'diaper'] as const;
export type EventType = (typeof EVENT_TYPES)[number];

export const DIAPER_STATUSES = ['wet', 'dirty', 'mixed'] as const;
export type DiaperStatus = (typeof DIAPER_STATUSES)[number];

export const SLEEP_KINDS = ['nap', 'night'] as const;
export type SleepKind = (typeof SLEEP_KINDS)[number];

/** Provenance for imported rows; never used for logic other than audit. */
export const SourceInfoSchema = z
  .object({
    system: z.string().max(64),
    file: z.string().max(255),
    row: z.number().int().nonnegative(),
    baby: z.string().max(255).optional(),
    raw_time: z.string().max(64).optional(),
  })
  .strict();

const note = z.string().max(2000).default('');

export const FormulaPayloadSchema = z
  .object({
    amount_ml: z.number().finite().nonnegative(),
    note,
    source: SourceInfoSchema.optional(),
  })
  .strict();

export const SleepPayloadSchema = z
  .object({
    sleep_kind: z.enum(SLEEP_KINDS).nullable().default(null),
    note,
    source: SourceInfoSchema.optional(),
  })
  .strict();

export const DiaperPayloadSchema = z
  .object({
    status: z.enum(DIAPER_STATUSES),
    note,
    source: SourceInfoSchema.optional(),
  })
  .strict();

export type FormulaPayload = z.infer<typeof FormulaPayloadSchema>;
export type SleepPayload = z.infer<typeof SleepPayloadSchema>;
export type DiaperPayload = z.infer<typeof DiaperPayloadSchema>;

export type PayloadFor<T extends EventType> = T extends 'formula'
  ? FormulaPayload
  : T extends 'sleep'
    ? SleepPayload
    : DiaperPayload;

export const SYNC_STATES = ['local', 'syncing', 'synced', 'conflict', 'error'] as const;
export type SyncState = (typeof SYNC_STATES)[number];

const uuid = z.string().uuid();
const isoUtc = z.string().datetime({ offset: true });

/** Earliest plausible event time (guards against epoch/garbage dates). */
export const MIN_EVENT_MS = Date.UTC(2000, 0, 1);
/** Allowed clock skew into the future for new events. */
export const MAX_FUTURE_SKEW_MS = 24 * 60 * 60 * 1000;

const EventBase = z.object({
  id: uuid,
  household_id: uuid,
  baby_id: uuid,
  started_at: isoUtc,
  ended_at: isoUtc.nullable(),
  version: z.number().int().nonnegative(),
  created_by: uuid.nullable(),
  updated_by: uuid.nullable(),
  created_at: isoUtc,
  updated_at: isoUtc,
  deleted_at: isoUtc.nullable(),
  source_system: z.string().max(64).nullable(),
  source_key: z.string().max(128).nullable(),
});

export const EventRecordSchema = z
  .discriminatedUnion('type', [
    EventBase.extend({ type: z.literal('formula'), payload: FormulaPayloadSchema }),
    EventBase.extend({ type: z.literal('sleep'), payload: SleepPayloadSchema }),
    EventBase.extend({ type: z.literal('diaper'), payload: DiaperPayloadSchema }),
  ])
  .superRefine((e, ctx) => {
    const start = Date.parse(e.started_at);
    if (start < MIN_EVENT_MS) ctx.addIssue({ code: 'custom', path: ['started_at'], message: 'Start time is implausibly early' });
    if (e.ended_at !== null) {
      if (e.type !== 'sleep') ctx.addIssue({ code: 'custom', path: ['ended_at'], message: 'Only sleep events have an end time' });
      else if (Date.parse(e.ended_at) < start)
        ctx.addIssue({ code: 'custom', path: ['ended_at'], message: 'End must be at or after start' });
    }
    if ((e.source_system === null) !== (e.source_key === null))
      ctx.addIssue({ code: 'custom', path: ['source_key'], message: 'source_system and source_key must be set together' });
  });

/** Server-shaped event record (what is synced). */
export type EventRecord = z.infer<typeof EventRecordSchema>;

/** Local IndexedDB row: server record + local-only indexing/sync fields. */
export type LocalEvent = EventRecord & {
  started_ms: number;
  ended_ms: number | null;
  sync_state: SyncState;
  sync_error?: string | null;
};

export function isCalendarDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T00:00:00Z`);
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value;
}

export const BabyRecordSchema = z
  .object({
    id: uuid,
    household_id: uuid,
    display_name: z.string().trim().min(1).max(80),
    date_of_birth: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .refine(isCalendarDate, 'Enter a real calendar date')
      .nullable(),
    timezone: z.string().min(1).max(64),
    version: z.number().int().nonnegative(),
    created_at: isoUtc,
    updated_at: isoUtc,
    deleted_at: isoUtc.nullable(),
  })
  .strict();

export type BabyRecord = z.infer<typeof BabyRecordSchema>;
export type LocalBaby = BabyRecord & { sync_state: SyncState; sync_error?: string | null };

export function payloadSchemaFor(type: EventType) {
  switch (type) {
    case 'formula':
      return FormulaPayloadSchema;
    case 'sleep':
      return SleepPayloadSchema;
    case 'diaper':
      return DiaperPayloadSchema;
  }
}

/** Strip local-only fields to get the server/wire shape. */
export function toEventRecord(e: LocalEvent): EventRecord {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { started_ms, ended_ms, sync_state, sync_error, ...rec } = e;
  void started_ms; void ended_ms; void sync_state; void sync_error;
  return rec as EventRecord;
}

export function toBabyRecord(b: LocalBaby): BabyRecord {
  const { sync_state, sync_error, ...rec } = b;
  void sync_state; void sync_error;
  return rec;
}

export function fromEventRecord(rec: EventRecord, sync_state: SyncState): LocalEvent {
  return {
    ...rec,
    started_ms: Date.parse(rec.started_at),
    ended_ms: rec.ended_at ? Date.parse(rec.ended_at) : null,
    sync_state,
    sync_error: null,
  };
}

/** Soft warning thresholds (not hard blocks) for formula volume entry. */
export const FORMULA_WARN_ML = 300;
